/**
 * Marcações vetoriais de dano do checklist — gerador de shapes compartilhado.
 *
 * CONTRATO. As coordenadas em `markingsJson` são NORMALIZADAS (0..1) relativas
 * ao retângulo onde o mecânico desenhou. Toda superfície que renderize precisa
 * reproduzir esse retângulo: imagem em `contain` dentro dele e overlay com as
 * dimensões do RETÂNGULO, nunca da janela.
 *
 * Qual retângulo é esse depende da versão da marcação, e a própria marcação diz
 * qual — ver `markingsAspect`:
 *
 * - **Com `ar`**: o retângulo é a FOTO, na proporção `ar` (largura/altura). O
 *   desenho cobre a imagem inteira e nada mais, então a marcação cai no mesmo
 *   ponto da lataria em qualquer superfície: app, web, share page.
 * - **Sem `ar` (legado)**: o retângulo é o box 4:3 fixo que o editor usava até
 *   a v1.8.2, com a foto em `contain` dentro dele. A foto do celular em retrato
 *   é mais alta que larga, então ela ocupava só uma faixa central desse box e
 *   as tarjas cinza dos lados entravam na conta. Mantido como está: mudar a
 *   interpretação do que já foi gravado deslocaria marcação de OS antiga.
 *
 * O `ar` viaja DENTRO de cada shape, e não num envelope, de propósito: o
 * `markingsJson` trafega como string opaca (`z.string()` no /media/confirm,
 * `z.any()` no backoffice) e três leitores fazem `JSON.parse` esperando um
 * ARRAY. Um envelope `{v, ar, shapes}` quebraria esses leitores no intervalo
 * entre o app atualizar e a API subir; um campo a mais no objeto passa
 * intocado por todos eles. O custo é repetir um número por shape.
 *
 * Consumidores: share-page SSR (markingsToSvg) e web-admin/ui kit
 * (markingsToShapes → JSX). O mobile mantém o componente
 * react-native-svg próprio (mesma matemática).
 */

/**
 * Categoria da avaria. Chave gravada: não se reinterpreta o que já foi
 * gravado (D12 do plano dos módulos verticais).
 *
 *   - `scratch`, `dirt`, `other` — o checklist de 6 ângulos, igual para toda
 *     oficina. `scratch` foi rotulado "Amassado" na captura e "Arranhão/
 *     Amassado" no resto, então mistura as duas avarias: marcação histórica
 *     não dá para reclassificar;
 *   - `amassado`, `risco`, `ferrugem`, `trincado` — a foto de PEÇA da
 *     funilaria (F3.2), onde a diferença entre risco e amassado decide entre
 *     martelinho e repintura. Oferecidas só lá, por decisão do dono do produto
 *     (24/09): o checklist das oficinas de mecânica não muda.
 *
 *   - `swirl`, `mancha`, `oxidacao`, `cratera` (+ `risco`) — o checklist da
 *     OS de ESTÉTICA (F2.3): o laudo de entrada do detailer registra defeito de
 *     PINTURA, não de lataria. "Swirl" (marcas circulares de polimento mal
 *     feito) e mancha de chuva ácida são o que o cliente vai reclamar que
 *     "apareceu depois" — e é isso que a foto marcada na entrada prova.
 *
 * Aparelho que não conhece uma chave desenha com a cor de `other`
 * (`damageColor` no app, `markingsToShapes` aqui) — por isso o fallback subiu
 * antes das chaves novas.
 */
export type DamageCategory =
  | 'scratch'
  | 'dirt'
  | 'other'
  | 'amassado'
  | 'risco'
  | 'ferrugem'
  | 'trincado'
  | 'swirl'
  | 'mancha'
  | 'oxidacao'
  | 'cratera';

/** O que a marcação do checklist de 6 ângulos oferece — igual para toda oficina. */
export const CHECKLIST_DAMAGE_CATEGORIES = ['scratch', 'dirt', 'other'] as const satisfies readonly DamageCategory[];

/** O que a marcação da foto de peça (funilaria) oferece. */
export const PANEL_DAMAGE_CATEGORIES = [
  'amassado',
  'risco',
  'ferrugem',
  'trincado',
  'other',
] as const satisfies readonly DamageCategory[];

/** O que a marcação do checklist da OS de estética oferece (F2.3). */
export const ESTETICA_DAMAGE_CATEGORIES = [
  'risco',
  'swirl',
  'mancha',
  'oxidacao',
  'cratera',
  'other',
] as const satisfies readonly DamageCategory[];

/** Rótulo completo — pills, legendas, leitor de tela. */
export const DAMAGE_CATEGORY_LABELS: Record<DamageCategory, string> = {
  scratch: 'Arranhão/Amassado',
  dirt: 'Sujeira/Desgaste',
  other: 'Outro',
  amassado: 'Amassado',
  risco: 'Risco',
  ferrugem: 'Ferrugem',
  trincado: 'Trincado',
  swirl: 'Swirl (marca de polimento)',
  mancha: 'Mancha / chuva ácida',
  oxidacao: 'Oxidação do verniz',
  cratera: 'Cratera no verniz',
};
export type DamageTool = 'circle' | 'arrow' | 'freehand';

export interface MarkingShape {
  id: string;
  tool: DamageTool;
  category: DamageCategory;
  // circle: cx/cy normalizados; r normalizado em relação à largura
  cx?: number;
  cy?: number;
  r?: number;
  // arrow
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  // freehand
  points?: { x: number; y: number }[];
  /**
   * Proporção (largura/altura) da FOTO contra a qual estas coordenadas foram
   * normalizadas. Ausente = marcação legada, normalizada contra o box 4:3.
   * Denormalizado por shape — ver o cabeçalho do arquivo.
   */
  ar?: number;
}

/**
 * Traço da marcação sobre a foto. Cor de sinalização sobre lataria, não cor de
 * marca (Regra 12 trata da identidade): o que importa é distinguir as
 * categorias umas das outras numa foto qualquer. As novas ficam longe das três
 * antigas para uma foto de peça nunca parecer marcada com categoria de
 * checklist.
 */
export const DAMAGE_MARKING_COLORS: Record<DamageCategory, string> = {
  scratch: '#ef4444',
  dirt: '#eab308',
  other: '#3b82f6',
  amassado: '#ec4899',
  risco: '#f97316',
  ferrugem: '#a16207',
  trincado: '#a855f7',
  swirl: '#84cc16',
  mancha: '#6366f1',
  oxidacao: '#78716c',
  cratera: '#d946ef',
};

/** Primitivas de desenho já escaladas para o box de destino. */
export type MarkingDrawOp =
  | { kind: 'circle'; key: string; cx: number; cy: number; r: number; color: string }
  | { kind: 'line'; key: string; x1: number; y1: number; x2: number; y2: number; color: string }
  | { kind: 'polyline'; key: string; points: string; color: string };

/**
 * Converte marcações normalizadas em primitivas de desenho escaladas
 * para `width × height` (as dimensões do box 4:3 de destino).
 * Mesma matemática do DamageMarkingsOverlay do mobile.
 */
export function markingsToShapes(
  markings: MarkingShape[],
  width: number,
  height: number,
): MarkingDrawOp[] {
  const ops: MarkingDrawOp[] = [];

  for (const m of markings ?? []) {
    const color = DAMAGE_MARKING_COLORS[m.category] ?? DAMAGE_MARKING_COLORS.other;

    if (m.tool === 'circle' && m.cx != null && m.cy != null && m.r != null) {
      ops.push({
        kind: 'circle',
        key: m.id,
        cx: m.cx * width,
        cy: m.cy * height,
        r: m.r * width,
        color,
      });
      continue;
    }

    if (m.tool === 'arrow' && m.x1 != null && m.y1 != null && m.x2 != null && m.y2 != null) {
      const ax1 = m.x1 * width;
      const ay1 = m.y1 * height;
      const ax2 = m.x2 * width;
      const ay2 = m.y2 * height;
      const dx = ax2 - ax1;
      const dy = ay2 - ay1;
      const len = Math.sqrt(dx * dx + dy * dy);
      const headLen = Math.min(20, len * 0.3);
      const a = Math.atan2(dy, dx);
      const a1 = a + Math.PI * 0.8;
      const a2 = a - Math.PI * 0.8;
      ops.push(
        { kind: 'line', key: `${m.id}-shaft`, x1: ax1, y1: ay1, x2: ax2, y2: ay2, color },
        {
          kind: 'line',
          key: `${m.id}-h1`,
          x1: ax2,
          y1: ay2,
          x2: ax2 + headLen * Math.cos(a1),
          y2: ay2 + headLen * Math.sin(a1),
          color,
        },
        {
          kind: 'line',
          key: `${m.id}-h2`,
          x1: ax2,
          y1: ay2,
          x2: ax2 + headLen * Math.cos(a2),
          y2: ay2 + headLen * Math.sin(a2),
          color,
        },
      );
      continue;
    }

    if (m.tool === 'freehand' && m.points && m.points.length > 1) {
      ops.push({
        kind: 'polyline',
        key: m.id,
        points: m.points.map((p) => `${p.x * width},${p.y * height}`).join(' '),
        color,
      });
    }
  }

  return ops;
}

const round = (n: number) => Math.round(n * 100) / 100;

/**
 * Renderiza as marcações como string `<svg>` (SSR — share page).
 * O SVG deve ser posicionado em `absolute inset-0` sobre o box 4:3.
 */
export function markingsToSvg(
  markings: MarkingShape[],
  width: number,
  height: number,
  strokeWidth = 3,
): string {
  const ops = markingsToShapes(markings, width, height);
  if (ops.length === 0) return '';

  const els = ops
    .map((op) => {
      if (op.kind === 'circle') {
        return `<circle cx="${round(op.cx)}" cy="${round(op.cy)}" r="${round(op.r)}" stroke="${op.color}" stroke-width="${strokeWidth}" fill="none"/>`;
      }
      if (op.kind === 'line') {
        return `<line x1="${round(op.x1)}" y1="${round(op.y1)}" x2="${round(op.x2)}" y2="${round(op.y2)}" stroke="${op.color}" stroke-width="${strokeWidth}" stroke-linecap="round"/>`;
      }
      return `<polyline points="${op.points}" stroke="${op.color}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`;
    })
    .join('');

  return `<svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" style="position:absolute;inset:0;width:100%;height:100%;pointer-events:none">${els}</svg>`;
}

/**
 * Proporção do retângulo em que estas marcações devem ser desenhadas.
 *
 * Devolve o `ar` da primeira shape que o tenha — todas as shapes de uma foto
 * são gravadas juntas, com o mesmo valor. Sem `ar`, devolve `null`: é marcação
 * legada e cabe a quem chama usar `LEGACY_MARKING_ASPECT`.
 */
export function markingsAspect(markings: readonly MarkingShape[] | null | undefined): number | null {
  for (const m of markings ?? []) {
    if (typeof m.ar === 'number' && Number.isFinite(m.ar) && m.ar > 0) return m.ar;
  }
  return null;
}

/** O box 4:3 fixo que o editor usou até a v1.8.2. */
export const LEGACY_MARKING_ASPECT = 4 / 3;

/**
 * Altura do retângulo de desenho para uma dada largura — a conta única que
 * todas as superfícies precisam fazer para posicionar foto e overlay.
 *
 * A marcação manda: é ela que diz contra qual retângulo suas coordenadas foram
 * normalizadas. `photoAspect` só entra quando não há marcação nenhuma (nada a
 * sobrepor, então é só enquadrar a foto sem tarja) e quem chama conhece as
 * dimensões reais — hoje só o app do mecânico, no fluxo de abertura de OS.
 * Sem nenhum dos dois, o box legado 4:3.
 */
export function markingsBoxHeight(
  width: number,
  markings: readonly MarkingShape[] | null | undefined,
  photoAspect?: number | null,
): number {
  // Havendo marcação, ela decide sozinha — inclusive quando é legada e não
  // traz `ar`. Deixar o `photoAspect` entrar aí reinterpretaria coordenada já
  // gravada contra outro retângulo, que é exatamente o deslocamento que este
  // código existe para evitar.
  const aspect =
    (markings?.length ?? 0) > 0
      ? markingsAspect(markings) ?? LEGACY_MARKING_ASPECT
      : photoAspect;

  return width / (aspect && aspect > 0 ? aspect : LEGACY_MARKING_ASPECT);
}

/** Parse defensivo do markingsJson vindo do banco. Inválido/ausente → []. */
export function parseMarkingsJson(raw: unknown): MarkingShape[] {
  if (typeof raw !== 'string' || raw.length === 0) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as MarkingShape[]) : [];
  } catch {
    return [];
  }
}
